import { isDefined, isString } from "@clipboard-health/util-ts";
import { CardContent, Link, Stack, type SxProps, type Theme } from "@mui/material";

import { type IntentVariant } from "../../../ui-theme";
import { mergeSxProps } from "../../lib/mergeSxProps";
import { sanitizeHtml, type UnsanitizedHtml } from "../../lib/sanitizeHtml";
import { CardWithIntent } from "../CardWithIntent";
import { IconButton } from "../IconButton";
import { Illustration, type IllustrationType } from "../Illustration";
import { Text, type TextProps } from "../Text";

export interface BannerProps {
  illustrationType: IllustrationType;
  title: string;
  /** Knock Guides bodies are in HTML. */
  description: string | UnsanitizedHtml;
  onClose?: () => void;
  intent?: IntentVariant;
  children?: React.ReactNode;
  wrapperSx?: SxProps<Theme>;
}

export function Banner(props: BannerProps) {
  const {
    illustrationType,
    title,
    description,
    onClose,
    intent = "neutral",
    children,
    wrapperSx,
  } = props;

  return (
    <CardWithIntent intent={intent} sx={mergeSxProps({ flexShrink: 0 }, wrapperSx)}>
      <CardContent
        sx={{
          position: "relative",
          paddingX: 5,
          paddingTop: 5,
          "&:last-child": {
            paddingBottom: 6, // CardContent has a default padding bottom to the last child, so we override it here
          },
        }}
      >
        {isDefined(onClose) && (
          <IconButton
            iconType="close"
            size="medium"
            variant="ghost"
            LinkComponent={Link}
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
            }}
            onClick={onClose}
          />
        )}

        <Stack direction="column" alignItems="center" spacing={5}>
          <Illustration type={illustrationType} />

          <Text semibold variant="h5" sx={{ textAlign: "center", textWrap: "balance" }}>
            {title}
          </Text>
          <Description description={description} />

          {children}
        </Stack>
      </CardContent>
    </CardWithIntent>
  );
}

function Description(props: { description: BannerProps["description"] }) {
  const { description } = props;

  const sharedProps: TextProps = {
    sx: { textAlign: "center", paddingX: 7, textWrap: "balance" },
    variant: "body2",
  };
  return isString(description) ? (
    <Text {...sharedProps}>{description}</Text>
  ) : (
    <Text {...sharedProps} dangerouslySetInnerHTML={sanitizeHtml(description)} />
  );
}
